import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

type AuthMode = 'signin' | 'signup';

export default function AuthPage() {
    const [mode, setMode] = useState<AuthMode>('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    const { signIn, signUp, signInWithGoogle, user, googleAuthPending, authError, clearAuthError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = (location.state as { returnTo?: string })?.returnTo || '/';

    // If user becomes authenticated, navigate away
    useEffect(() => {
        if (user) {
            console.log('[AuthPage] User authenticated, navigating to:', returnTo);
            navigate(returnTo);
        }
    }, [user, navigate, returnTo]);

    // Clear auth error when component mounts or mode changes
    useEffect(() => {
        clearAuthError();
        setLocalError(null);
    }, [mode, clearAuthError]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        setLocalError(null);
        clearAuthError();

        try {
            if (mode === 'signin') {
                await signIn(email, password);
            } else {
                await signUp(email, password);
            }
            // Navigation handled by useEffect when user state changes
        } catch (err) {
            console.error('[AuthPage] Email auth error:', err);
            setLocalError(err instanceof Error ? err.message : 'Authentication failed');
        } finally {
            setLoading(false);
        }
    }

    async function handleGoogleSignIn() {
        setLocalError(null);
        clearAuthError();

        try {
            console.log('[AuthPage] Starting Google sign-in');
            await signInWithGoogle();
            // Navigation handled by useEffect when user state changes
        } catch (err) {
            console.error('[AuthPage] Google sign-in error:', err);
            // Error is handled by authError from context
        }
    }

    // Show loading if either local loading or Google auth is pending
    const isLoading = loading || googleAuthPending;

    // Combine local error and context auth error
    const displayError = localError || authError;

    return (
        <div className="min-h-screen bg-primary-50 flex flex-col">
            {/* Header */}
            <div className="p-4">
                <button
                    onClick={() => navigate('/')}
                    className="text-gray-500 hover:text-gray-700"
                >
                    ← Back
                </button>
            </div>

            <main className="flex-1 flex items-center justify-center px-4">
                <div className="w-full max-w-sm">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-white text-3xl">♻️</span>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {mode === 'signin' ? 'Welcome back!' : 'Create account'}
                        </h1>
                        <p className="text-gray-500 mt-1">
                            {mode === 'signin' ? 'Sign in to continue' : 'Start recycling smarter'}
                        </p>
                    </div>

                    {/* Error Display */}
                    {displayError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm mb-4">
                            {displayError}
                        </div>
                    )}

                    {/* Google Sign In */}
                    <button
                        onClick={handleGoogleSignIn}
                        disabled={isLoading}
                        className="w-full bg-white border border-gray-300 rounded-xl py-3 px-4 flex items-center justify-center gap-3 font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {googleAuthPending ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Signing in...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Continue with Google
                            </>
                        )}
                    </button>

                    {/* Divider */}
                    <div className="flex items-center gap-4 my-6">
                        <div className="flex-1 h-px bg-gray-300" />
                        <span className="text-gray-400 text-sm">or</span>
                        <div className="flex-1 h-px bg-gray-300" />
                    </div>

                    {/* Email Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                placeholder="••••••••"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Loading...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
                        </button>
                    </form>

                    {/* Mode Toggle */}
                    <p className="text-center mt-6 text-gray-600">
                        {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
                        <button
                            onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
                            className="text-primary-600 font-semibold hover:text-primary-700"
                        >
                            {mode === 'signin' ? 'Sign up' : 'Sign in'}
                        </button>
                    </p>
                </div>
            </main>
        </div>
    );
}
